import type { PropsWithChildren } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Logo } from '@/components/atoms/Logo';
import { paths } from '@/routes/paths';

const HIGHLIGHTS = [
  'Monitoraggio backup, firewall, antivirus e syslog in un unico hub',
  'Ticket con SLA e aggiornamenti in tempo reale',
  'Report e assistente AI sui tuoi dati di servizio',
];

export interface AuthLayoutProps extends PropsWithChildren {
  title: string;
  subtitle?: string;
}

/**
 * Split layout for unauthenticated pages: marketing panel on the left (hidden
 * below `md`, where the form should own the viewport) and the form on the right.
 */
export function AuthLayout({ title, subtitle, children }: AuthLayoutProps): JSX.Element {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex' }}>
      <Box
        aria-hidden="true"
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          width: '44%',
          maxWidth: 560,
          p: 10,
          backgroundColor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          color: 'text.primary',
        }}
      >
        <Logo size="large" to={paths.home} />

        <Box>
          <Typography variant="h1" sx={{ fontSize: '2.25rem', mb: 3 }}>
            La sicurezza dei tuoi servizi, sempre sotto controllo.
          </Typography>

          <Stack spacing={3} sx={{ mt: 6 }}>
            {HIGHLIGHTS.map((item) => (
              <Stack key={item} direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 20, mt: 0.25, color: 'secondary.light' }} />
                <Typography variant="body2" sx={{ opacity: 0.92 }}>
                  {item}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          © {new Date().getFullYear()} Wenkey. Tutti i diritti riservati.
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 4, sm: 8 },
          backgroundColor: 'background.default',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, mb: 6, justifyContent: 'center' }}>
            <Logo size="medium" to={paths.home} />
          </Box>

          <Card sx={{ p: { xs: 5, sm: 7 } }}>
            <Typography variant="h2" component="h1" sx={{ fontSize: '1.5rem' }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                {subtitle}
              </Typography>
            )}
            <Box sx={{ mt: 6 }}>{children}</Box>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}
